import * as React from 'react';
import {
    Box,
    Button,
    Dialog,
    Stack,
    TablePagination,
    Tooltip,
    Typography,
} from '@mui/material';
import Filter from 'component/filter';
import colors from 'assets/theme/base/colors';
import borders from 'assets/theme/base/borders';
import FlexibleTable from 'component/flexible-table';
import { useEffect, useState } from 'react';
import { RowDataType, TableMetaData } from 'types/common/common';
import { useAppDispatch } from 'hooks/redux';
import { ReactComponent as Edit } from 'assets/images/staff-user/edit.svg';
import { ReactComponent as Delete } from 'assets/images/staff-user/delete.svg';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import { openConfirmationDialogs } from 'store/slices/confirmation-dialog/confirmation-dialog-slice';
import AddImages from 'component/add-images';
import { CardsPersonageData } from 'types/card/card-personage-list';
import pxToRem from 'assets/theme/function/px-to-rem';
import { openSnackbar } from 'store/slices/snackbar/snackbar-slice';
import {
    useCardCategoryQuery,
    useLazyCardListQuery,
} from 'store/slices/cards/card/card-list';
import { CardData, CardListRequest } from 'types/card/card-list';
import AddCard from 'pages/card/add-card';
import { usePersonageTitleListQuery } from 'store/slices/cards/personage/personage-list';
import FilterCard from 'pages/card/filter-card/index';
import EditCard from 'pages/card/edit-card';
import { useCardImageUpdateMutation } from 'store/slices/cards/card/add-card-images';
import { ImageTypeEnum } from 'types/card/card';
import { useCardActiveMutation } from 'store/slices/cards/card/active-card';
import { getStatusColor } from 'component/status-style';
import { StyledTableRow } from 'component/tabel-pagination-style';
import { useCardDeleteMutation } from 'store/slices/cards/card/delete-archive-card';
import { API_ENDPOINT_URL } from 'constant';
import ViewPhoto from 'component/view-photo';
import { openBackdrop } from 'store/slices/backdrop/backdrop-slice';
import GamepadIcon from '@mui/icons-material/Gamepad';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    addNewCardData,
    deleteCardData,
} from 'store/slices/cards/card/card-data-slice';
import { deleteCategoryId } from 'store/slices/toon-tint/toon-tint-category-id-slice';

export const initialListData: CardListRequest = {
    page: 1,
    pageSize: 10,
    sort: 'createdAt',
};

const defaultTableStaffDataList: TableMetaData[] = [
    {
        key: 'img',
        position: 1,
        title: 'Image',
        isSortable: false,
    },

    {
        key: 'title',
        position: 2,
        title: 'Name',
        isSortable: true,
    },

    {
        key: 'personageTitle',
        position: 3,
        title: 'Personage Name',
        isSortable: false,
    },

    {
        key: 'sphereTitle',
        position: 4,
        title: 'Sphere Name',
        isSortable: false,
    },
    {
        key: 'code',
        position: 5,
        title: 'Code',
        isSortable: true,
    },
    {
        key: 'cardCategory',
        position: 6,
        title: 'Card Category',
        isSortable: true,
    },
    {
        key: 'positionInCollection',
        position: 7,
        title: 'Position In Collection',
        isSortable: true,
    },
    {
        key: 'createdAt',
        position: 8,
        title: 'Created Data',
        isSortable: true,
        type: 'date',
        format: 'DD.MM.YYYY HH:mm',
    },

    {
        key: 'status',
        position: 9,
        title: 'Status',
        isSortable: false,
    },

    {
        key: 'action',
        position: 10,
        title: 'Action',
        isSortable: false,
    },
];

export type UrlObject = {
    url?: string;
    title?: string;
};

const Card: React.FC = () => {
    const [openAddCardDialog, setOpenAddCardDialog] = React.useState(false);
    const [tableMetaDataList, setTableMetaDataList] = useState<TableMetaData[]>(
        defaultTableStaffDataList,
    );
    const [openEditCardDialog, setOpenEditCardDialog] = React.useState(false);
    const [openImgDialog, setOpenImgDialog] = React.useState(false);
    const [cardId, setCardId] = React.useState<CardsPersonageData>();
    const [cardStatus, setCardStatus] = React.useState<string>('');
    const [dataCardRequestList, setDataCardRequestList] =
        React.useState<CardListRequest>(initialListData);
    const [pagesCount, setPagesCount] = useState<number>(0);
    const [page, setPage] = useState<number>(initialListData.page);
    const [pageSize, setPageSize] = useState<number>(initialListData.pageSize);
    const [sort, setSort] = useState<string>(initialListData.sort);
    const [tableData, setTableData] = useState<RowDataType[]>([]);
    const [viewPhotoUrl, setViewPhotoUrl] = useState<UrlObject | null>(null);

    const [
        cardImagesUpdateApi,
        { isSuccess, isError: imageIsError, error: imageError },
    ] = useCardImageUpdateMutation();

    const [getCardList, { data, isFetching }] = useLazyCardListQuery();
    const { data: personageList } = usePersonageTitleListQuery();
    const { data: cardCategoryList } = useCardCategoryQuery();

    const [
        cardDeleteApi,
        {
            error: deleteError,
            isError: deleteIsError,
            isSuccess: deleteIsSuccess,
        },
    ] = useCardDeleteMutation();

    const [
        statusActiveApi,
        {
            error: activeError,
            isError: activeIsError,
            isSuccess: activeIsSuccess,
        },
    ] = useCardActiveMutation();

    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const location = useLocation();
    const params = new URLSearchParams(location.search);

    const createNewCard = () => {
        setOpenAddCardDialog(true);
    };

    const updateImages = (images: FormData) => {
        cardImagesUpdateApi({
            cardId: cardId?.id,
            image: images,
        });
    };

    const deleteCard = ({ id }: CardData) => {
        if (id) {
            cardDeleteApi(id);
        }
    };

    const getCardListFunction = () => {
        getCardList({
            ...dataCardRequestList,
            sort: sort === '' ? initialListData.sort : sort,
            page: page,
            pageSize: pageSize,
        });

        navigate(location.pathname, { replace: true });
    };

    const handelClickEditCardPersonage = (cardId: CardData) => {
        setOpenEditCardDialog(true);
        setCardId(cardId);
        setCardStatus(cardId.status ?? '');
    };

    const handelClickStatus = (cardId: string) => {
        statusActiveApi(cardId);
    };

    const openPictureDialogFun = (cardId: CardData) => {
        setCardId(cardId);
        setOpenImgDialog(true);
    };

    const toonTintDetails = (card: CardData) => {
        if (!card) return;

        dispatch(
            addNewCardData({
                fileId: card.fileId,
                cardName: card.cardName,
                allowedGamesCard: card.allowedGamesCard,
                code: card.code,
            }),
        );

        const queryObj: Record<string, string> = {};
        Object.entries(dataCardRequestList).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
                queryObj[key] = String(value);
            }
        });

        const query = new URLSearchParams(queryObj).toString();

        navigate(`/card/toon-tint/${card.id}${query ? `?${query}` : ''}`);
    };

    const handleFilterData = (currentPage = 0) => {
        setPage(currentPage + 1);
    };

    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        setPageSize(Number(event.target.value));
        setPage(1);
    };

    useEffect(() => {
        setPagesCount(
            data && data?.data.length > 0 ? Number(data?.rowsCount) : 0,
        );
        const tableData: RowDataType[] = data
            ? data.data.map((fullInfo) => {
                  return {
                      ...fullInfo,
                      status: (
                          <Box>
                              <Button
                                  variant={'contained'}
                                  size={'small'}
                                  sx={{
                                      bgcolor: getStatusColor(fullInfo.status),
                                      pointerEvents:
                                          fullInfo.status !== 'draft' &&
                                          fullInfo.status !== 'empty'
                                              ? 'none'
                                              : 'auto',
                                  }}
                                  onClick={() => {
                                      dispatch(
                                          openConfirmationDialogs({
                                              textInfo:
                                                  'Are You Sure Want To Active Card',
                                              handleConfirm: () =>
                                                  handelClickStatus(
                                                      fullInfo.id,
                                                  ),
                                          }),
                                      );
                                  }}
                              >
                                  {fullInfo.status}
                              </Button>
                          </Box>
                      ),
                      img: (
                          <>
                              {fullInfo?.fileId ? (
                                  <Box
                                      component="img"
                                      src={`${API_ENDPOINT_URL}/file/file/${fullInfo?.fileId}?source=${ImageTypeEnum.CARD}`}
                                      alt=""
                                      onClick={() => {
                                          setViewPhotoUrl({
                                              url: `${API_ENDPOINT_URL}/file/file/${fullInfo?.fileId}?source=${ImageTypeEnum.CARD}`,
                                              title: fullInfo.title,
                                          });
                                      }}
                                      sx={{
                                          cursor: 'pointer',
                                          width: 80,
                                          height: 60,
                                          objectFit: 'contain',
                                          objectPosition: 'top',
                                          borderRadius: borders.borderRadius.md,
                                      }}
                                  />
                              ) : (
                                  <Typography variant={'h5'}>-</Typography>
                              )}
                          </>
                      ),
                      action: (
                          <>
                              <Box
                                  display={'flex'}
                                  justifyContent={'center'}
                                  gap={2}
                              >
                                  {fullInfo.status === 'draft' && (
                                      <Tooltip title="Delete">
                                          <Delete
                                              cursor="pointer"
                                              onClick={() => {
                                                  dispatch(
                                                      openConfirmationDialogs({
                                                          textInfo:
                                                              'Are You Sure Want To Delete Card',
                                                          handleConfirm:
                                                              deleteCard,
                                                          parameters: {
                                                              id: fullInfo.id,
                                                          },
                                                      }),
                                                  );
                                              }}
                                          />
                                      </Tooltip>
                                  )}
                                  <Tooltip title="Edit">
                                      <Edit
                                          cursor="pointer"
                                          onClick={() =>
                                              handelClickEditCardPersonage({
                                                  fileId: fullInfo.id,
                                                  id: fullInfo.id,
                                                  status: fullInfo.status,
                                              })
                                          }
                                      />
                                  </Tooltip>
                                  <Tooltip title="Add photo">
                                      <AddPhotoAlternateIcon
                                          titleAccess={'Add photo'}
                                          cursor="pointer"
                                          onClick={() =>
                                              openPictureDialogFun({
                                                  fileId: fullInfo.fileId,
                                                  id: fullInfo.id,
                                                  imageType: ImageTypeEnum.CARD,
                                              })
                                          }
                                      />
                                  </Tooltip>
                                  <Tooltip title="Toon tint">
                                      <GamepadIcon
                                          titleAccess="Toon tint"
                                          cursor="pointer"
                                          onClick={() =>
                                              toonTintDetails({
                                                  fileId: fullInfo.fileId,
                                                  id: fullInfo.id,
                                                  cardName: fullInfo.title,
                                                  code: fullInfo.code,
                                                  allowedGamesCard:
                                                      fullInfo.allowedGamesCard
                                                          ? [
                                                                fullInfo.allowedGamesCard,
                                                            ]
                                                          : [],
                                                  imageType: ImageTypeEnum.CARD,
                                              })
                                          }
                                      />
                                  </Tooltip>
                              </Box>
                          </>
                      ),
                      rowComponent: StyledTableRow,
                  };
              })
            : [];
        setTableData(tableData);
    }, [data]);

    useEffect(() => {
        if (dataCardRequestList) {
            getCardListFunction();
        }
    }, [dataCardRequestList, page, pageSize, sort]);

    useEffect(() => {
        if (deleteIsSuccess || isSuccess || activeIsSuccess) {
            setOpenImgDialog(false);
            getCardListFunction();
        }
    }, [deleteIsSuccess, isSuccess, activeIsSuccess]);

    useEffect(() => {
        dispatch(openBackdrop({ open: isFetching }));
    }, [isFetching]);

    useEffect(() => {
        if (data) {
            setPagesCount(0);
            /* setTableData([]);*/
            setPage(1);
        }
    }, [dataCardRequestList]);

    useEffect(() => {
        if (location.pathname === '/card') {
            dispatch(deleteCardData());
            dispatch(deleteCategoryId());
        }

        const filteredParams = Object.fromEntries(
            Array.from(params.entries()).filter(([key, value]) => key && value),
        );

        if (Object.keys(filteredParams).length > 3) {
            setDataCardRequestList((prev) => ({
                ...prev,
                ...filteredParams,
                page: prev.page,
                pageSize: prev.pageSize,
                sort: prev.sort,
            }));
        }
    }, [location.pathname]);

    useEffect(() => {
        const error = deleteError || imageError || activeError;

        if (error && 'data' in error && error.data?.message) {
            dispatch(
                openSnackbar({
                    message: error.data.message,
                    severity: 'error',
                    open: true,
                    position: {
                        vertical: 'top',
                        horizontal: 'right',
                    },
                }),
            );
        }
    }, [deleteIsError, imageIsError, activeIsError]);

    return (
        <Box>
            <Filter>
                <FilterCard
                    setDataPersonageRequestList={setDataCardRequestList}
                    cardCategoryList={cardCategoryList}
                />
            </Filter>
            <Box
                bgcolor={colors.secondary.light}
                borderRadius={borders.borderRadius.md}
                mt={pxToRem(7)}
            >
                <FlexibleTable
                    tableMetaDataList={tableMetaDataList}
                    setTableMetaDataList={setTableMetaDataList}
                    tableDataList={tableData}
                    sort={sort}
                    setSort={setSort}
                    createNewFun={createNewCard}
                    buttonName={'Creat New Card'}
                />

                {pagesCount > 0 && (
                    <Stack spacing={2} alignItems={'end'}>
                        <TablePagination
                            rowsPerPageOptions={[10, 30, 50]}
                            component="div"
                            count={pagesCount}
                            rowsPerPage={pageSize}
                            page={page - 1}
                            labelRowsPerPage={'Number of rows'}
                            slotProps={{
                                select: {
                                    inputProps: {
                                        'aria-label': 'rows per page',
                                    },
                                    MenuProps: {
                                        disablePortal: true,
                                        slotProps: {
                                            paper: {
                                                sx: {
                                                    backgroundColor:
                                                        colors.secondary.main,
                                                    padding: 0,
                                                },
                                            },
                                        },
                                    },
                                    native: false,
                                },
                            }}
                            onPageChange={(_, page) => handleFilterData(page)}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                        />
                    </Stack>
                )}
            </Box>

            {openAddCardDialog && (
                <AddCard
                    refetch={getCardListFunction}
                    openDialog={openAddCardDialog}
                    setOpenDialog={setOpenAddCardDialog}
                    personageList={personageList}
                    cardCategoryList={cardCategoryList}
                />
            )}

            {cardId?.id && (
                <>
                    <Dialog
                        open={openImgDialog}
                        onClose={() => {
                            setOpenImgDialog(false);
                        }}
                    >
                        <AddImages
                            imageUrl={[
                                cardId.fileId
                                    ? `${API_ENDPOINT_URL}/file/file/${cardId.fileId}?source=${cardId.imageType}`
                                    : undefined,
                            ]}
                            setOpenImgDialog={setOpenImgDialog}
                            onSubmitFun={updateImages}
                            dialogTitle={'CARD IMAGE'}
                        />
                    </Dialog>
                </>
            )}

            {openEditCardDialog && cardId?.id && (
                <Dialog
                    open={openEditCardDialog}
                    onClose={() => {
                        setOpenEditCardDialog(false);
                    }}
                >
                    <EditCard
                        refetch={getCardListFunction}
                        cardId={cardId.fileId}
                        setOpenEditCardDialog={setOpenEditCardDialog}
                        statusList={cardCategoryList}
                        cardStatus={cardStatus}
                    />
                </Dialog>
            )}

            {viewPhotoUrl && (
                <ViewPhoto
                    open={Boolean(viewPhotoUrl)}
                    setOpen={() => setViewPhotoUrl(null)}
                    imageUrl={viewPhotoUrl}
                />
            )}
        </Box>
    );
};

export default Card;
